import React, { useState, useContext } from 'react';
import { LanguageContext } from '../App';
import { ChevronDownIcon, PlusIcon, MinusIcon, MapPinIcon, PackageIcon, TruckIcon, CreditCardIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { pageTransition, fadeIn, slideUp, staggerContainer, listItem, buttonHover, buttonTap, scaleIn } from '../utils/animations';
export const ManualBooking: React.FC = () => {
  const {
    language
  } = useContext(LanguageContext);
  const [destinations, setDestinations] = useState([{
    id: 1
  }]);
  const addDestination = () => {
    const newId = destinations.length > 0 ? Math.max(...destinations.map(d => d.id)) + 1 : 1;
    setDestinations([...destinations, {
      id: newId
    }]);
  };
  const removeDestination = (id: number) => {
    if (destinations.length > 1) {
      setDestinations(destinations.filter(d => d.id !== id));
    }
  };
  return <motion.div className="space-y-6" variants={pageTransition} initial="hidden" animate="visible" exit="exit" data-id="element-885">
      <motion.div className="flex justify-between items-center" variants={slideUp} data-id="element-886">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-887">
          {language === 'en' ? 'Manual Booking' : 'الحجز اليدوي'}
        </h2>
      </motion.div>
      <motion.div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" variants={fadeIn} data-id="element-888">
        <motion.div className="px-6 py-4 border-b border-gray-200" variants={slideUp} data-id="element-889">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-890">
            {language === 'en' ? 'Create New Delivery Order' : 'إنشاء طلب توصيل جديد'}
          </h3>
        </motion.div>
        <motion.div className="p-6" variants={slideUp} data-id="element-891">
          <form className="space-y-8" data-id="element-892">
            {/* Customer Details */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-893">
              <motion.h4 className="text-md font-medium text-gray-900 border-b pb-2" variants={listItem} data-id="element-894">
                {language === 'en' ? 'Customer Details' : 'تفاصيل العميل'}
              </motion.h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-895">
                <motion.div variants={listItem} data-id="element-896">
                  <label htmlFor="customer" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-897">
                    {language === 'en' ? 'Select Customer' : 'اختر العميل'}
                  </label>
                  <div className="relative" data-id="element-898">
                    <select id="customer" className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-899">
                      <option value="" data-id="element-900">
                        {language === 'en' ? 'Select a customer' : 'اختر عميل'}
                      </option>
                      <option value="USR-5623" data-id="element-901">
                        Ahmed Al-Sabah (USR-5623)
                      </option>
                      <option value="USR-5622" data-id="element-902">
                        Fatima Al-Qadiri (USR-5622)
                      </option>
                      <option value="USR-5621" data-id="element-903">
                        Mohammed Al-Mutairi (USR-5621)
                      </option>
                      <option value="USR-5620" data-id="element-904">
                        Sarah Al-Enezi (USR-5620)
                      </option>
                    </select>
                    <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-905" />
                  </div>
                </motion.div>
                <motion.div variants={listItem} data-id="element-906">
                  <label htmlFor="newCustomer" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-907">
                    {language === 'en' ? 'Or Create New Customer' : 'أو أنشئ عميل جديد'}
                  </label>
                  <motion.button type="button" id="newCustomer" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" whileHover={buttonHover} whileTap={buttonTap} data-id="element-908">
                    <PlusIcon size={16} className="mr-2" data-id="element-909" />
                    {language === 'en' ? 'Add New Customer' : 'إضافة عميل جديد'}
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
            {/* Service Type */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-910">
              <motion.h4 className="text-md font-medium text-gray-900 border-b pb-2" variants={listItem} data-id="element-911">
                {language === 'en' ? 'Service Type' : 'نوع الخدمة'}
              </motion.h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-912">
                <motion.div variants={listItem} data-id="element-913">
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-914">
                    {language === 'en' ? 'Select Service Type' : 'اختر نوع الخدمة'}
                  </label>
                  <div className="relative" data-id="element-915">
                    <select id="serviceType" className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-916">
                      <option value="express" data-id="element-917">
                        {language === 'en' ? 'Express Delivery' : 'توصيل سريع'}
                      </option>
                      <option value="standard" data-id="element-918">
                        {language === 'en' ? 'Standard Delivery' : 'توصيل قياسي'}
                      </option>
                      <option value="scheduled" data-id="element-919">
                        {language === 'en' ? 'Scheduled Delivery' : 'توصيل مجدول'}
                      </option>
                    </select>
                    <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-920" />
                  </div>
                </motion.div>
                <motion.div variants={listItem} data-id="element-921">
                  <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-922">
                    {language === 'en' ? 'Vehicle Type' : 'نوع المركبة'}
                  </label>
                  <div className="relative" data-id="element-923">
                    <select id="vehicleType" className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-924">
                      <option value="motorcycle" data-id="element-925">
                        {language === 'en' ? 'Motorcycle' : 'دراجة نارية'}
                      </option>
                      <option value="car" data-id="element-926">
                        {language === 'en' ? 'Car' : 'سيارة'}
                      </option>
                    </select>
                    <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-927" />
                  </div>
                </motion.div>
              </div>
            </motion.div>
            {/* Pickup Location */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-928">
              <motion.h4 className="text-md font-medium text-gray-900 border-b pb-2" variants={listItem} data-id="element-929">
                {language === 'en' ? 'Pickup Location' : 'موقع الاستلام'}
              </motion.h4>
              <div className="grid grid-cols-1 gap-6" data-id="element-930">
                <motion.div variants={listItem} data-id="element-931">
                  <label htmlFor="pickupAddress" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-932">
                    {language === 'en' ? 'Pickup Address' : 'عنوان الاستلام'}
                  </label>
                  <div className="relative" data-id="element-933">
                    <input type="text" id="pickupAddress" placeholder={language === 'en' ? 'Enter full address' : 'أدخل العنوان الكامل'} className="block w-full px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-934" />
                    <MapPinIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-935" />
                  </div>
                </motion.div>
                <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={staggerContainer} data-id="element-936">
                  <motion.div variants={listItem} data-id="element-937">
                    <label htmlFor="pickupArea" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-938">
                      {language === 'en' ? 'Area' : 'المنطقة'}
                    </label>
                    <div className="relative" data-id="element-939">
                      <select id="pickupArea" className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-940">
                        <option value="kuwait-city" data-id="element-941">
                          {language === 'en' ? 'Kuwait City' : 'مدينة الكويت'}
                        </option>
                        <option value="salmiya" data-id="element-942">
                          {language === 'en' ? 'Salmiya' : 'السالمية'}
                        </option>
                        <option value="hawally" data-id="element-943">
                          {language === 'en' ? 'Hawally' : 'حولي'}
                        </option>
                        <option value="jabriya" data-id="element-944">
                          {language === 'en' ? 'Jabriya' : 'الجابرية'}
                        </option>
                      </select>
                      <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-945" />
                    </div>
                  </motion.div>
                  <motion.div variants={listItem} data-id="element-946">
                    <label htmlFor="pickupBlock" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-947">
                      {language === 'en' ? 'Block' : 'قطعة'}
                    </label>
                    <input type="text" id="pickupBlock" className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-948" />
                  </motion.div>
                  <motion.div variants={listItem} data-id="element-949">
                    <label htmlFor="pickupBuilding" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-950">
                      {language === 'en' ? 'Building/House No.' : 'رقم المبنى/المنزل'}
                    </label>
                    <input type="text" id="pickupBuilding" className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-951" />
                  </motion.div>
                </motion.div>
                <motion.div variants={listItem} data-id="element-952">
                  <label htmlFor="pickupNotes" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-953">
                    {language === 'en' ? 'Additional Notes' : 'ملاحظات إضافية'}
                  </label>
                  <textarea id="pickupNotes" rows={2} className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" placeholder={language === 'en' ? 'Any special instructions for pickup' : 'أي تعليمات خاصة للاستلام'} data-id="element-954"></textarea>
                </motion.div>
              </div>
            </motion.div>
            {/* Delivery Destinations */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-955">
              <div className="flex justify-between items-center border-b pb-2" data-id="element-956">
                <motion.h4 className="text-md font-medium text-gray-900" variants={listItem} data-id="element-957">
                  {language === 'en' ? 'Delivery Destinations' : 'وجهات التوصيل'}
                </motion.h4>
                <motion.button type="button" onClick={addDestination} className="inline-flex items-center px-3 py-1 border border-congress-blue-300 rounded-md text-sm font-medium text-congress-blue-700 bg-congress-blue-50 hover:bg-congress-blue-100" whileHover={buttonHover} whileTap={buttonTap} variants={listItem} data-id="element-958">
                  <PlusIcon size={16} className="mr-1" data-id="element-959" />
                  {language === 'en' ? 'Add Destination' : 'إضافة وجهة'}
                </motion.button>
              </div>
              <AnimatePresence data-id="element-960">
                {destinations.map((destination, index) => <motion.div key={destination.id} className="p-4 border border-gray-200 rounded-md space-y-4" variants={scaleIn} initial="hidden" animate="visible" exit="exit" layout data-id="element-961">
                    <div className="flex justify-between items-center" data-id="element-962">
                      <h5 className="text-sm font-medium text-gray-900" data-id="element-963">
                        {language === 'en' ? `Destination ${index + 1}` : `الوجهة ${index + 1}`}
                      </h5>
                      {destinations.length > 1 && <motion.button type="button" onClick={() => removeDestination(destination.id)} className="inline-flex items-center px-2 py-1 text-sm text-red-600 hover:text-red-800" whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} data-id="element-964">
                          <MinusIcon size={16} className="mr-1" data-id="element-965" />
                          {language === 'en' ? 'Remove' : 'إزالة'}
                        </motion.button>}
                    </div>
                    <div className="grid grid-cols-1 gap-4" data-id="element-966">
                      <div data-id="element-967">
                        <label htmlFor={`destAddress-${destination.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-968">
                          {language === 'en' ? 'Delivery Address' : 'عنوان التوصيل'}
                        </label>
                        <div className="relative" data-id="element-969">
                          <input type="text" id={`destAddress-${destination.id}`} placeholder={language === 'en' ? 'Enter full address' : 'أدخل العنوان الكامل'} className="block w-full px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-970" />
                          <MapPinIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-971" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-972">
                        <div data-id="element-973">
                          <label htmlFor={`destArea-${destination.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-974">
                            {language === 'en' ? 'Area' : 'المنطقة'}
                          </label>
                          <div className="relative" data-id="element-975">
                            <select id={`destArea-${destination.id}`} className="appearance-none block w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-976">
                              <option value="kuwait-city" data-id="element-977">
                                {language === 'en' ? 'Kuwait City' : 'مدينة الكويت'}
                              </option>
                              <option value="salmiya" data-id="element-978">
                                {language === 'en' ? 'Salmiya' : 'السالمية'}
                              </option>
                              <option value="hawally" data-id="element-979">
                                {language === 'en' ? 'Hawally' : 'حولي'}
                              </option>
                              <option value="jabriya" data-id="element-980">
                                {language === 'en' ? 'Jabriya' : 'الجابرية'}
                              </option>
                            </select>
                            <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-981" />
                          </div>
                        </div>
                        <div data-id="element-982">
                          <label htmlFor={`destBlock-${destination.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-983">
                            {language === 'en' ? 'Block' : 'قطعة'}
                          </label>
                          <input type="text" id={`destBlock-${destination.id}`} className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-984" />
                        </div>
                        <div data-id="element-985">
                          <label htmlFor={`destBuilding-${destination.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-986">
                            {language === 'en' ? 'Building/House No.' : 'رقم المبنى/المنزل'}
                          </label>
                          <input type="text" id={`destBuilding-${destination.id}`} className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-987" />
                        </div>
                      </div>
                      <div data-id="element-988">
                        <label htmlFor={`destNotes-${destination.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-989">
                          {language === 'en' ? 'Delivery Notes' : 'ملاحظات التوصيل'}
                        </label>
                        <textarea id={`destNotes-${destination.id}`} rows={2} className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" placeholder={language === 'en' ? 'Any special instructions for delivery' : 'أي تعليمات خاصة للتوصيل'} data-id="element-990"></textarea>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </motion.div>
            {/* Package Details */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-991">
              <motion.h4 className="text-md font-medium text-gray-900 border-b pb-2" variants={listItem} data-id="element-992">
                {language === 'en' ? 'Package Details' : 'تفاصيل الطرد'}
              </motion.h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-993">
                <motion.div variants={listItem} data-id="element-994">
                  <label htmlFor="packageType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-995">
                    {language === 'en' ? 'Package Type' : 'نوع الطرد'}
                  </label>
                  <div className="relative" data-id="element-996">
                    <select id="packageType" className="appearance-none block w-full px-10 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-997">
                      <option value="document" data-id="element-998">
                        {language === 'en' ? 'Document' : 'مستند'}
                      </option>
                      <option value="small-package" data-id="element-999">
                        {language === 'en' ? 'Small Package' : 'طرد صغير'}
                      </option>
                      <option value="medium-package" data-id="element-1000">
                        {language === 'en' ? 'Medium Package' : 'طرد متوسط'}
                      </option>
                      <option value="large-package" data-id="element-1001">
                        {language === 'en' ? 'Large Package' : 'طرد كبير'}
                      </option>
                    </select>
                    <PackageIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1002" />
                    <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1003" />
                  </div>
                </motion.div>
                <motion.div variants={listItem} data-id="element-1004">
                  <label htmlFor="packageWeight" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1005">
                    {language === 'en' ? 'Package Weight (kg)' : 'وزن الطرد (كجم)'}
                  </label>
                  <input type="number" id="packageWeight" min="0" step="0.1" className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1006" />
                </motion.div>
              </div>
              <motion.div className="flex items-center space-x-4" variants={listItem} data-id="element-1007">
                <div className="flex items-center" data-id="element-1008">
                  <input id="isFragile" type="checkbox" className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-1009" />
                  <label htmlFor="isFragile" className="ml-2 block text-sm text-gray-700" data-id="element-1010">
                    {language === 'en' ? 'Fragile' : 'قابل للكسر'}
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1011">
                  <input id="requiresSignature" type="checkbox" className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-1012" />
                  <label htmlFor="requiresSignature" className="ml-2 block text-sm text-gray-700" data-id="element-1013">
                    {language === 'en' ? 'Requires Signature' : 'يتطلب توقيع'}
                  </label>
                </div>
              </motion.div>
            </motion.div>
            {/* Payment Details */}
            <motion.div className="space-y-6" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-1014">
              <motion.h4 className="text-md font-medium text-gray-900 border-b pb-2" variants={listItem} data-id="element-1015">
                {language === 'en' ? 'Payment Details' : 'تفاصيل الدفع'}
              </motion.h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1016">
                <motion.div variants={listItem} data-id="element-1017">
                  <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1018">
                    {language === 'en' ? 'Payment Method' : 'طريقة الدفع'}
                  </label>
                  <div className="relative" data-id="element-1019">
                    <select id="paymentMethod" className="appearance-none block w-full px-10 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1020">
                      <option value="wallet" data-id="element-1021">
                        {language === 'en' ? 'Wallet' : 'المحفظة'}
                      </option>
                      <option value="cash" data-id="element-1022">
                        {language === 'en' ? 'Cash on Delivery' : 'الدفع عند الاستلام'}
                      </option>
                      <option value="credit-card" data-id="element-1023">
                        {language === 'en' ? 'Credit Card' : 'بطاقة ائتمان'}
                      </option>
                    </select>
                    <CreditCardIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1024" />
                    <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1025" />
                  </div>
                </motion.div>
                <motion.div variants={listItem} data-id="element-1026">
                  <label htmlFor="deliveryFee" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1027">
                    {language === 'en' ? 'Delivery Fee (KWD)' : 'رسوم التوصيل (د.ك)'}
                  </label>
                  <input type="number" id="deliveryFee" min="0" step="0.1" value="3.5" readOnly className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1028" />
                </motion.div>
              </div>
            </motion.div>
            {/* Submit Buttons */}
            <motion.div className="flex justify-end space-x-4" variants={fadeIn} data-id="element-1029">
              <motion.button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" whileHover={buttonHover} whileTap={buttonTap} data-id="element-1030">
                {language === 'en' ? 'Cancel' : 'إلغاء'}
              </motion.button>
              <motion.button type="button" className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-congress-blue-600 hover:bg-congress-blue-700" whileHover={buttonHover} whileTap={buttonTap} data-id="element-1031">
                {language === 'en' ? 'Create Order' : 'إنشاء الطلب'}
              </motion.button>
            </motion.div>
          </form>
        </motion.div>
      </motion.div>
    </motion.div>;
};